import React, { createContext, useState, ReactNode } from 'react';

type ModalType = 'alert' | 'confirm';

interface ModalState {
  isOpen: boolean;
  type: ModalType;
  message: string;
  resolve?: (value: boolean) => void;
}

interface ModalContextType {
  modalState: ModalState;
  showAlert: (message: string) => Promise<void>;
  showConfirm: (message: string) => Promise<boolean>;
  closeModal: (result?: boolean) => void;
}

export const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const ModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    type: 'alert',
    message: '',
  });

  const showAlert = (message: string): Promise<void> => {
    return new Promise((resolve) => {
      setModalState({
        isOpen: true,
        type: 'alert',
        message,
        resolve: () => {
          resolve();
        },
      });
    });
  };

  const showConfirm = (message: string): Promise<boolean> => {
    return new Promise((resolve) => {
      setModalState({
        isOpen: true,
        type: 'confirm',
        message,
        resolve,
      });
    });
  };

  const closeModal = (result: boolean = false) => {
    if (modalState.resolve) {
      modalState.resolve(result);
    }
    setModalState({
      isOpen: false,
      type: 'alert',
      message: '',
    });
  };

  return (
    <ModalContext.Provider value={{ modalState, showAlert, showConfirm, closeModal }}>
      {children}
    </ModalContext.Provider>
  );
};
